/**
 * Summary-card status fold (NAU-263).
 *
 * The four summary cards are green/red only in v1 (no amber): a card is green
 * only when nothing needs attention, and ANY attention-needed item — an overdue
 * or missed check, a flagged issue, an open action, or an incomplete completion
 * rate — turns it red. The priority list keeps the full three-band amber from
 * `traffic-light.ts`; only the cards fold to two colours.
 */

import type { DashboardStatusMetricsDto } from "@/generated/model";
import type { DashboardKpis } from "@/lib/dashboard/dashboard-overview";
import type { DashboardStatus } from "@/lib/dashboard/traffic-light";

/** A count-based card (Missed/Overdue, Issues, Open Actions) is green at zero, red otherwise. */
export function cardStatusFromCount(count: number): DashboardStatus {
  return count > 0 ? "ACTION" : "ON_TRACK";
}

/** The Completion card is green only when everything due is done, red while anything is outstanding. */
export function completionCardStatus(completionRate: number): DashboardStatus {
  return completionRate >= 100 ? "ON_TRACK" : "ACTION";
}

/**
 * Percentage-point change in completion rate vs the previous period, rounded.
 * Null when there is no basis for a comparison: the previous period has not
 * loaded yet, or nothing was due in it (an empty period folds to a vacuous 100%,
 * so a delta against it would be misleading rather than informative). The single
 * definition shared by the completion summary card (NAU-263) and the insights
 * improvement tile (NAU-266), so the two surfaces reconcile by construction.
 */
export function completionRateDelta(
  currentRate: number,
  previousRate: number | undefined,
  previousTotalDue: number | undefined,
): number | null {
  if (previousRate === undefined || !previousTotalDue) {
    return null;
  }
  return Math.round(currentRate - previousRate);
}

/**
 * Map the backend dashboard metrics onto the four card view-models, applying the
 * green/red fold. The counts come from the same status evaluation that feeds the
 * priority list and today's-status, so the cards reconcile by construction.
 */
export function kpisFromMetrics(
  metrics: DashboardStatusMetricsDto,
  deltaVsLastWeek: number | null,
): DashboardKpis {
  return {
    completion: {
      rate: metrics.completionRate,
      completed: metrics.completedCount,
      total: metrics.totalDueCount,
      status: completionCardStatus(metrics.completionRate),
      deltaVsLastWeek,
    },
    missedOverdue: {
      value: metrics.missedOverdueTotal,
      status: cardStatusFromCount(metrics.missedOverdueTotal),
    },
    issuesFlagged: {
      value: metrics.issuesFlaggedTotal,
      status: cardStatusFromCount(metrics.issuesFlaggedTotal),
    },
    openActions: {
      value: metrics.openActionsTotal,
      status: cardStatusFromCount(metrics.openActionsTotal),
    },
  };
}

/** Zero-value metrics, used while the query is loading or a field is missing (defensive bind). */
export const EMPTY_DASHBOARD_METRICS: DashboardStatusMetricsDto = {
  completionRate: 100,
  completedCount: 0,
  totalDueCount: 0,
  missedOverdueTotal: 0,
  issuesFlaggedTotal: 0,
  openActionsTotal: 0,
  lateChecksTotal: 0,
  mostActive: null,
};
